import React from "react";
import { useNavigate } from "react-router-dom";
import { Video } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

const TC_STATUS_BADGES = {
  scheduled: { bg: "#DBEAFE", text: "#1E40AF", label: "Scheduled" },
  awaiting_participants: { bg: "#FEF3C7", text: "#92400E", label: "Awaiting" },
  in_progress: { bg: "#D1FAE5", text: "#065F46", label: "In Progress" },
  completed: { bg: "#F1F5F9", text: "#475569", label: "Completed" },
  cancelled: { bg: "#FEE2E2", text: "#991B1B", label: "Cancelled" },
  no_show: { bg: "#FED7AA", text: "#9A3412", label: "No Show" },
};

export default function PatientTeleconsultTab({ patientId }) {
  const [consults, setConsults] = React.useState([]);
  const [encounters, setEncounters] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const navigate = useNavigate();

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/teleconsult/consultations", { params: { patient_id: patientId, page_size: 50 } }),
      api.get("/teleconsult/encounters", { params: { patient_id: patientId } }),
    ])
      .then(([c, e]) => { setConsults(c.data.items || []); setEncounters(e.data.items || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId]);
  React.useEffect(() => { load(); }, [load]);

  async function startNow() {
    setCreating(true);
    try {
      const r = await api.post("/teleconsult/consultations", {
        patient_id: patientId, consultation_type: "follow_up",
        priority: "routine", duration_minutes: 30, source_module: "manual",
        notes: "Initiated from patient profile.",
      });
      toast.success("Consultation created");
      navigate(`/teleconsult/room/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  }

  return (
    <div className="space-y-4" data-testid="patient-teleconsult-tab">
      <div className="hah-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#1F2937]">Teleconsultations</h3>
          <Button size="sm" onClick={startNow} disabled={creating}
            className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="patient-tc-start">
            <Video className="w-3.5 h-3.5 mr-1.5" /> Start Teleconsult
          </Button>
        </div>
        {loading ? <div className="text-sm text-[#6B7280]">Loading…</div> : consults.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No teleconsultations yet.</div>
        ) : (
          <div className="space-y-2">
            {consults.map((c) => {
              const b = TC_STATUS_BADGES[c.status] || TC_STATUS_BADGES.scheduled;
              return (
                <button key={c.id} onClick={() => navigate(`/teleconsult/room/${c.id}`)}
                  className="w-full text-left p-3 border border-[#E5E7EB] rounded-lg hover:bg-[#EAF8FA]/40 flex items-center gap-3"
                  data-testid={`patient-tc-row-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1F2937] capitalize">{(c.consultation_type || "").replace(/_/g, " ")}</span>
                      <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{c.source_module || "manual"}</span>
                    </div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "Unscheduled"}
                      {c.provider_name ? ` · ${c.provider_name}` : ""}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
                    style={{ background: b.bg, color: b.text }}>{b.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="hah-card p-5">
        <h4 className="font-semibold text-[#1F2937] mb-2">Recent encounters</h4>
        {encounters.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No encounters recorded.</div>
        ) : (
          <ul className="space-y-3 text-sm">
            {encounters.slice(0, 8).map((e) => (
              <li key={e.id} className="border-b border-[#E5E7EB] pb-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#1F2937] capitalize">{(e.consultation_type || "").replace(/_/g, " ")}</span>
                  <span className="text-xs text-[#6B7280]">{e.ended_at ? new Date(e.ended_at).toLocaleString() : "—"} · {e.duration_minutes ?? 0}min</span>
                </div>
                {e.assessment && <div className="text-xs text-[#6B7280] mt-1 line-clamp-2">{e.assessment}</div>}
                {e.diagnosis && <div className="text-xs text-[#0B7E8C] mt-0.5">Dx: {e.diagnosis}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
